import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { rootApi, type SessionUser } from './api.js';

/// Quem é a pessoa, perguntado em dois lugares que precisam concordar: o portão da rota, antes de
/// montar a tela, e a própria tela depois de montada. Declarar a consulta uma vez é o que impede
/// os dois de usarem chaves diferentes — chave divergente aqui seria a rota liberar a entrada com
/// uma sessão que a tela não enxerga.
export const sessionQuery = queryOptions({
  queryKey: ['auth', 'session'],
  queryFn: rootApi.session,
  retry: false,
  /// O tempo de frescor é declarado aqui, e não deixado para o padrão do cliente, porque agora
  /// existem dois consumidores em sequência: o portão da rota pergunta, e a tela que ele acabou de
  /// liberar monta e pergunta de novo. Sem isto, toda navegação custaria duas idas ao servidor para
  /// responder a mesma coisa.
  staleTime: 30_000,
});

/// Falhar ao perguntar quem é não é o mesmo que não ser ninguém — mas na tela dá no mesmo, e é
/// exatamente o que o sistema fazia antes de existir rota: a consulta sem resposta caía no login.
/// Deixar o erro subir daqui trocaria a tela de entrada por uma tela de erro no dia em que a API
/// oscilasse, o que é pior: quem não consegue entrar tenta de novo, e para tentar precisa do
/// formulário.
export async function loadSession(queryClient: QueryClient): Promise<SessionUser | null> {
  try {
    return await queryClient.ensureQueryData(sessionQuery);
  } catch {
    return null;
  }
}

/// Perguntar de novo quem é, e **esperar** a resposta nova antes de decidir para onde ir.
///
/// Invalidar não servia para isso. `ensureQueryData` devolve o que está guardado sempre que há algo
/// guardado — e "ninguém" é algo: quem chega na entrada grava `null` no cache, porque a sessão
/// respondida com 403 vira `null` em vez de erro. Depois do MFA a invalidação apenas marcava esse
/// `null` como velho; o portão do início lia o velho, concluía que não havia sessão e devolvia a
/// pessoa para a entrada — que é onde ela já estava. Daí o "cliquei em entrar e não aconteceu
/// nada", com o desafio consumido do lado do servidor e o refresh entrando direto.
///
/// Só quem tem uma tela olhando a consulta escapava, porque aí a invalidação buscava de verdade.
/// Isto aqui não depende de haver alguém olhando: busca sempre, e resolve com o dado novo no cache.
export async function refreshSession(queryClient: QueryClient): Promise<SessionUser | null> {
  try {
    return await queryClient.fetchQuery({ ...sessionQuery, staleTime: 0 });
  } catch {
    return null;
  }
}

/// A entrada deu certo no servidor, mas o navegador não devolveu a sessão na pergunta seguinte —
/// o caso de interface e API em sites diferentes, em que o cookie não acompanha as chamadas. A tela
/// de entrada mostra este motivo em vez de recomeçar calada.
export class SessionNotEstablishedError extends Error {
  public constructor() {
    super(
      'A entrada foi aceita, mas o navegador não manteve a sessão. Entre de novo; se continuar, avise o suporte.',
    );
    this.name = 'SessionNotEstablishedError';
  }
}
